// Source dictionary. `Dictionary` is derived from it, so every other language
// must provide exactly the same keys (a missing or extra key is a type error).
// Backticks inside a string mark inline code, see renderInlineCode.
export const en = {
  header: {
    nav: {
      home: "Home",
      about: "About me",
      work: "Work",
      caseStudy: "Case study",
      contact: "Contact",
      cv: "CV",
    },
    openMenu: "Open menu",
    closeMenu: "Close menu",
    // Names the language the switch changes TO
    switchLanguageLabel: "Cambiar a español",
    toggleTheme: "Toggle light theme",
  },
  hero: {
    eyebrowBefore: "Hello",
    eyebrowAfter: ", I'm Darío Condori Humerez",
    headline: "I build mobile apps that people actually download.",
    ledeBefore:
      "Senior mobile engineer with 9+ years in software, 6+ of them shipping mobile apps to the App Store and Google Play. I also build live web products like MobiZ and POS. OK Delivery, the delivery app I launched during the pandemic, passed",
    ledeStrong: "10k downloads",
    ledeAfter: ".",
    contact: "Contact",
    readCase: "Read the OK Delivery case",
    downloadCv: "Download CV",
  },
  about: {
    heading: "About me",
    paragraphs: [
      "I'm Darío, a software engineer specialized in mobile: cross-platform with React Native for iOS and Android, and native Android with Kotlin and Jetpack Compose. I pick one or the other based on what the product needs, not on what I know best.",
      "In 2020, in the middle of lockdown, I built and launched OK Delivery so local restaurants could keep selling. It is still in production and has passed 10k downloads.",
      "Since then I've worked with startups across Latin America, including a YC-backed SaaS. I architected offline-first sync for field workers with zero connectivity, wrote a native Kotlin module to bridge a payment-terminal SDK into React Native, ran a JavaScript to TypeScript migration without pausing releases, and shipped in-app AI assistants backed by Python services.",
    ],
    skills: [
      {
        term: "Mobile",
        value:
          "React Native, Expo (EAS), native Android with Kotlin and Jetpack Compose, native modules in Kotlin and Swift, offline-first sync, push notifications, store releases",
      },
      {
        term: "Frontend",
        value: "React, TypeScript, Next.js, Redux Toolkit, React Query, GraphQL, Tailwind CSS",
      },
      {
        term: "Backend & AI",
        value:
          "Node.js, Python (FastAPI, Django), Firebase, Supabase, PostgreSQL, MongoDB, OpenAI API",
      },
      {
        term: "Practices",
        value: "Clean architecture, Jest and Testing Library, CI/CD, Sentry, Git Flow",
      },
    ],
    photoAlt: "Darío Condori Humerez",
  },
  portfolio: {
    heading: "Selected work",
    ossHeading: "Open source",
    ossIntro: "Tools I build to work better with AI coding agents.",
    sideHeading: "Some projects I've built",
    copy: "Copy",
    copied: "Copied",
    terminalLabel: (title: string) => `Example terminal session for ${title}`,
    linkLabels: {
      googlePlay: "Google Play",
      appStore: "App Store",
      github: "GitHub",
      npm: "npm",
      demo: "Live demo",
      website: "Live site",
      caseStudy: "Read the case study",
    },
    linkAria: {
      onPlatform: (title: string, platform: string) => `${title} on ${platform}`,
      demo: (title: string) => `${title} live demo`,
      website: (title: string) => `${title} live site`,
    },
    featured: {
      "ok-delivery": {
        title: "OK Delivery",
        stat: "Android · Google Play",
        description:
          "Food delivery app that I designed, built and launched during the 2020 lockdown, still in production six years later. It grew the business's orders and sales by about 60%. React Native client on Firebase with distance-based delivery pricing; iOS and web versions are in development.",
        mediaAlt: "OK Delivery home screen listing popular restaurants in El Alto, Bolivia",
        facts: [
          { value: "10k+", label: "downloads on Google Play" },
          { value: "~60%", label: "more orders and sales" },
          { value: "2020", label: "in production since" },
        ],
      },
      mobiz: {
        title: "MobiZ",
        stat: "Live in Bolivia",
        description:
          "Real-time map of taxis and trufis (shared taxis) near you. Riders open it without an account, see which drivers are available and how many seats are free, and message them on WhatsApp in one tap. Drivers switch their availability on and off. Installable as a PWA.",
        mediaAlt: "MobiZ landing page with a phone showing a live map of nearby taxis",
      },
      "zeconds-pos": {
        title: "POS",
        stat: "Live · subscription SaaS",
        description:
          "Point of sale that replaces the barcode scanner and the cash register with a phone. The camera reads EAN, UPC and QR codes, every sale updates the inventory, and one account runs several stores with admin and cashier roles. Works in the browser, nothing to install.",
        mediaAlt: "POS landing page with the cashier screen on a phone",
      },
    },
    openSource: {
      cdd: {
        title: "CDD — Comprehension-Driven Development",
        // The live npm version is prepended at runtime
        meta: ["MIT", "Zero dependencies"],
        description:
          "A comprehension gate for AI-written code. A pre-commit hook asks one targeted question about the riskiest part of your diff; anything you can't explain becomes a COMPREHENSION_DEBT marker, and a CI audit blocks the merge while debt is open.",
        installNote: "Then use `cdd check`, `cdd audit` and `cdd report`.",
        terminal: {
          // Literal tool output: identical in every language
          out: "comprehension micro-check · 2 file(s)",
          q: "Q: If Redis is down, redis.incr(key) throws. What happens to the user trying to log in?",
          answer: "> the error is unhandled, so they can't log in",
          // Literal tool output: identical in every language
          ok: "✓ matches the code — you understand this change",
        },
      },
      "voice-screen-assistant": {
        title: "Voice Screen Assistant",
        meta: ["MIT", "Python 3.9+", "Local-first"],
        description:
          "A voice assistant that sees your screen. Talk while you work: it captures your monitors, understands what you have open, and answers out loud like a senior dev sitting next to you. Works with any LLM, or fully local with Ollama.\n\nSay “teach me Kotlin” and it switches to instructor mode: one small exercise at a time, checked against the real code on your screen. No vendor SDKs — plain HTTP, so the model, the voice or the transcription each swap with two lines of config.",
        terminal: {
          out: "mic → speech-to-text → LLM + screenshots of your monitors → text-to-speech",
          ok: '"Want a hand with that email?"',
        },
      },
    },
    side: {
      "my-shop-online": {
        title: "My Shop Online",
        description:
          "Platform where a business builds its own branded online store, uploads its products and takes orders over WhatsApp. Every store gets its own link, with a free tier and paid subscriptions.",
      },
      "trading-signals": {
        title: "Bitcoin trading signals API",
        description:
          "API that streams buy and sell signals for Bitcoin computed from EMA crossovers, served over WebSocket.",
      },
      "netflix-clone": {
        title: "Netflix clone",
        description:
          "Responsive web application built with React, TypeScript, styled-components and Webpack.",
      },
    },
  },
  caseStudy: {
    eyebrow: "Case study",
    title: "OK Delivery: shipping a delivery app in the middle of a lockdown",
    facts: [
      {
        term: "Role",
        value:
          "Everything, end to end: signing up the restaurants, the brand and logo, UI design, development and releases",
      },
      { term: "Period", value: "2020 to today (still maintained)" },
      { term: "Platform", value: "Android, on Google Play. iOS and web in development" },
      {
        term: "Stack",
        value:
          "React Native, Firebase (Auth, Firestore, Cloud Messaging), Redux Toolkit, Google Maps Platform, Angular admin panel",
      },
      { term: "Result", value: "10k+ downloads · orders and sales up about 60%" },
    ],
    problem: {
      heading: "The problem",
      paragraphs: [
        "In 2020 the lockdown closed dining rooms in El Alto, Bolivia, overnight. Local restaurants still had kitchens and customers, but no way to take orders at a distance. I built OK Delivery to solve that, and I did all of it myself: I signed up the first restaurants, designed the logo and the interface, wrote the app and published it.",
      ],
    },
    decisions: {
      heading: "Technical decisions",
      items: [
        {
          claim: "Firebase instead of servers to operate.",
          body: "Authentication, the database and push notifications run on Firebase, called directly from the app, and order updates arrive through Firestore listeners. Working alone, having no servers to patch or scale mattered more than backend flexibility.",
        },
        {
          claim: "Delivery pricing from real distance, tunable without a release.",
          body: "The fee comes from the driving distance between restaurant and customer. The base fee and the per-kilometre rate live in Firestore, so prices change without waiting for a store review.",
        },
        {
          claim: "Money logic lives in pure, tested functions.",
          body: "Reports once showed orders with a delivery fee of zero, because the send button could be tapped before the fee finished calculating. I moved the rule into a pure function, `canSubmitOrder`, and covered it and the fee calculation with tests.",
        },
        {
          claim: "Keeping a six-year-old app publishable.",
          body: "Google Play now requires 16 KB memory page support for Android 15. That meant upgrading the whole core in one migration: React Native 0.74 to 0.77, Firebase SDK 18 to 21 and Redux Toolkit 1 to 2.",
        },
      ],
    },
    result: {
      heading: "Result",
      paragraphs: [
        "OK Delivery passed 10k downloads on Google Play and grew the business's orders and sales by about 60%. Six years after launch it is still in production.",
      ],
    },
    googlePlayAria: "OK Delivery on Google Play",
    back: "Back to selected work",
  },
  contact: {
    heading: "Contact",
    intro: "If you want to talk to me, don't hesitate to call me or send me a message.",
    emailAlt: "Email",
    phoneAlt: "Phone",
  },
  form: {
    heading: "Get in touch using the form",
    emailPlaceholder: "Email",
    messagePlaceholder: "Leave your message",
    emailErrorPrefix: "Email",
    messageErrorPrefix: "Message",
    submit: "Submit",
    successToast: "Email successfully sent!",
    thanks: "Thanks for getting in touch!",
    backToTop: "Back to the top",
  },
  footer: {
    madeWith: "This website was made with",
  },
};

export type Dictionary = typeof en;
